import { inject, NgModule } from '@angular/core';
import { Router, RouterModule, UrlTree } from '@angular/router';
import { SchedulerComponent } from './components';
import { AuthGuard } from './guard/auth-guard';
import { AuthZGuard } from './guard/authZ-guard';
import {
	DevicesComponent,
	FaqComponent,
	GroupsComponent,
	IndividualsComponent,
	MessageUIComponent,
	MonitorUIComponent,
	SendMessageComponent,
	SettingsComponent,
	TemplatesComponent,
	UserProfileComponent
} from './screens';

import { AccessDeniedComponent } from './screens/access-denied/access-denied.component';
import { ApplicationConstant } from './utilities/ApplicationConstant';
import { CommonRoutes } from './models/CommonRoute';
import { DataService } from './services';
import { map, catchError, shareReplay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

// Cache the feature flag check to avoid multiple HTTP calls
let cachedFeatureFlagCheck: Observable<boolean> | null = null;

/**
 * Guard function for default route that conditionally redirects based on the FF_MessageType
 * feature flag. When enabled, redirects to messageStatusV2. Otherwise, redirects to user-profile.
 *
 * Returns a UrlTree for proper Angular routing instead of imperative navigation.
 */
const defaultRouteGuard = (): Observable<UrlTree> => {
    const router = inject(Router);
    const dataService = inject(DataService);

    // Cache the feature flag check to avoid multiple HTTP calls
    if (!cachedFeatureFlagCheck) {
        cachedFeatureFlagCheck = dataService.getSetting('FF_MessageType').pipe(
            map((result: any) => {
                // Convert result to boolean
                return result === true || result === 'true';
            }),
            catchError(() => of(false)), // On error, default to false
            shareReplay(1) // Cache the result
        );
    }

    // Check FF_MessageType feature flag and return appropriate UrlTree
    return cachedFeatureFlagCheck.pipe(
        map((isEnabled: boolean) => {
            // If feature flag is enabled, redirect to message-status-v2
            if (isEnabled) {
                return router.parseUrl('/messageStatusV2');
            } else {
                return router.parseUrl('/user-profile');
            }
        })
    );
};

const routes: CommonRoutes = [ // <-- Establish custom routes
    {path: '', canActivate: [defaultRouteGuard], children: []},
    {path: 'success', component: MessageUIComponent, canActivate: [AuthGuard]},
    {path: 'user-profile', component: UserProfileComponent, canActivate: [AuthGuard]},
    {path: 'sendAlert', component: SendMessageComponent, canActivate: [AuthGuard]},
    {path: 'messageMonitor', component: MonitorUIComponent, canActivate: [AuthGuard]},
    {path: 'messageStatusV2', canActivate: [AuthGuard], loadChildren: () => import('./screens/message-status-v2/message-status-v2.module').then(m => m.MessageStatusV2Module)},
  
    // Temporarily lift ADMIN restrictions on Individual, Device and Group maintenance
    {
        path: 'individuals',
        component: IndividualsComponent,
        canActivate: [AuthZGuard],
        data: {permissions: [ApplicationConstant.getAdminRole()]}
    },
    {path: 'channels', component: DevicesComponent, canActivate: [AuthGuard]},
    {path: 'devices', component: DevicesComponent, canActivate: [AuthGuard]},  // , canActivate: [AuthZGuard], data: {permissions: [ApplicationConstant.admin]}},
    {path: 'groups', component: GroupsComponent, canActivate: [AuthGuard]},           // , canActivate: [AuthZGuard], data: {permissions: [ApplicationConstant.admin]}},
    {path: 'who', canActivate: [AuthGuard], loadChildren: () => import('./screens/sscwho/sscwho.module').then(m => m.SscwhoModule)},
    {path: 'templates', component: TemplatesComponent, canActivate: [AuthGuard]},
    {
        path: 'settings',
        component: SettingsComponent,
        canActivate: [AuthZGuard],
        data: {permissions: [ApplicationConstant.getAdminRole()]}
    },
    {path: 'sendMessage', component: SendMessageComponent, canActivate: [AuthGuard]},
    {path: 'sendMessage/id,value', component: SendMessageComponent, canActivate: [AuthGuard]},
    {path: 'schedule', component: SchedulerComponent},
    {path: 'faq', component: FaqComponent, canActivate: [AuthGuard]},
    {path: 'faq/:category/:question', component: FaqComponent, canActivate: [AuthGuard]},
    {path: 'restricted', component: AccessDeniedComponent},
    {path: '**', redirectTo: '/user-profile', pathMatch: 'full'}
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {enableTracing: false})],
    exports: [RouterModule]
})

export class AppRoutingModule {

}
